import { type HeroInclusionListItem, type HeroInclusionListQuery } from '@utazzo/shared';
import { UzBadge } from '@utazzo/ui';
import React from 'react';

import { HeroInclusionFormDrawer } from '../components/HeroInclusionFormDrawer';
import { useDeleteHeroInclusion, useHeroInclusionsList } from '../use-hero-inclusions';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';
import { resolveMedia } from '@/features/website-cms/components/media.api';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'isActive',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

/** Resolves a media id → preview URL and renders a small square thumbnail. */
const IconThumb = ({ id }: { id: string | null }): React.JSX.Element => {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    if (!id) {
      setUrl(null);
      return;
    }
    resolveMedia(id)
      .then((m) => !cancelled && setUrl(m.url))
      .catch(() => !cancelled && setUrl(null));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!id) return <span className="text-zinc-400">-</span>;
  return url ? (
    <img src={url} alt="" className="h-8 w-8 rounded object-cover" />
  ) : (
    <span className="text-zinc-300">…</span>
  );
};

export const HeroInclusionsListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN' || me?.role === 'OPS';
  const deleteInclusion = useDeleteHeroInclusion();

  const columns: ListScreenColumns<HeroInclusionListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<HeroInclusionListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'icon',
        header: 'Icon',
        width: '72px',
        cell: (row) => <IconThumb id={row.iconImageId} />,
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => <span className="font-medium text-zinc-900">{row.name}</span>,
      },
      {
        id: 'isActive',
        header: 'Status',
        cell: (row) => (
          <UzBadge tone={row.isActive ? 'success' : 'secondary'}>
            {row.isActive ? 'Active' : 'Inactive'}
          </UzBadge>
        ),
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        width: '80px',
        sortable: true,
        cell: (row) => <span>{row.sortOrder}</span>,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<HeroInclusionListItem, HeroInclusionListQuery>
      title="Hero Inclusions"
      description="Icon + name strip shown in the website hero (e.g. Flights, Hotels, Meals)."
      useList={useHeroInclusionsList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const isActive = state.filters.isActive;
        return {
          ...base,
          ...(isActive ? { isActive: isActive === 'true' } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add inclusion' }}
      renderDrawer={({ open, mode, row, close }) => (
        <HeroInclusionFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          inclusion={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteInclusion.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this inclusion?',
            description: `${row.name} will be soft-deleted and hidden from the website.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name (min 3 characters)',
        emptyTitle: 'No hero inclusions match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load hero inclusions',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
